import { CaretDown } from '@/shared/icons/phosphor'
import { type JSX, Show, children, createSignal, createUniqueId } from 'solid-js'

export type DisclosureInset = 'md' | 'lg'

export interface DisclosureProps {
  label: JSX.Element
  children?: JSX.Element
  open?: boolean | undefined
  defaultOpen?: boolean | undefined
  onOpenChange?: ((open: boolean) => void) | undefined
  inset?: DisclosureInset | undefined
  class?: string | undefined
}

const triggerInset: Record<DisclosureInset, string> = {
  md: 'px-4 py-3 sm:px-5',
  lg: 'px-4 py-3.5 sm:px-7 sm:py-4.5',
}

const panelInset: Record<DisclosureInset, string> = {
  md: 'px-4 pb-3 sm:px-5',
  lg: 'px-4 pb-3.5 sm:px-7 sm:pb-4.5',
}

export function Disclosure(props: DisclosureProps) {
  const panelId = createUniqueId()
  const [ownOpen, setOwnOpen] = createSignal(props.defaultOpen ?? false)
  const open = () => props.open ?? ownOpen()
  const panel = children(() => props.children)
  const inset = () => props.inset ?? 'md'

  const toggle = () => {
    const next = !open()
    setOwnOpen(next)
    props.onOpenChange?.(next)
  }

  return (
    <div data-disclosure data-open={open() ? '' : undefined} class={props.class}>
      <button
        type="button"
        aria-expanded={open()}
        aria-controls={panel() ? panelId : undefined}
        onClick={toggle}
        class={`flex w-full cursor-pointer items-start gap-3 text-left text-[length:var(--text-sm)] leading-relaxed text-[color:var(--color-text)] transition-colors duration-[var(--duration-normal)] hover:bg-[var(--color-neutral-100)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-primary-500)] motion-reduce:transition-none ${triggerInset[inset()]}`}
      >
        <span data-disclosure-label class="flex min-w-0 flex-1 items-start gap-3">
          {props.label}
        </span>
        <span aria-hidden="true" class="flex h-[1lh] shrink-0 items-center">
          <CaretDown
            data-disclosure-caret
            class="h-4 w-4 text-[color:var(--color-text-muted)] transition-transform duration-[var(--duration-normal)] motion-reduce:transition-none"
            classList={{ 'rotate-180': open() }}
          />
        </span>
      </button>
      <Show when={panel()}>
        <div
          id={panelId}
          data-disclosure-panel
          hidden={!open()}
          class={`text-[length:var(--text-sm)] text-[color:var(--color-text-muted)] ${panelInset[inset()]}`}
        >
          {panel()}
        </div>
      </Show>
    </div>
  )
}
